import { useEffect, useEffectEvent, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { InsuredDependentsFormFields } from './DependentsFormInputs';
import { InsuredSpouseFormFields } from './SpouseFormInputs';

type Props = {
  employeeAddress: string | undefined;
  spouseForm: UseFormReturn<InsuredSpouseFormFields>;
  dependentsForm: UseFormReturn<InsuredDependentsFormFields>;
  clearSpousePossibleFipsCodes: () => void;
  clearDependentPossibleFipsCodes: (index: number) => void;
  isEnabled?: boolean;
};

/**
 * Copies the employee's address to the spouse and dependents who still share
 * it, judged by what each form holds now, so an address the user gave a member
 * survives the employee moving, typing on, or being rewritten on submit.
 */
export const useFollowEmployeeAddress = ({
  employeeAddress,
  spouseForm,
  dependentsForm,
  clearSpousePossibleFipsCodes,
  clearDependentPossibleFipsCodes,
  isEnabled = true,
}: Props) => {
  const previousEmployeeAddress = useRef(employeeAddress);

  const copyToMembersFollowing = useEffectEvent(
    (previous: string | undefined, current: string) => {
      const isFollowingEmployee = (memberAddress?: string) =>
        memberAddress !== current &&
        (!memberAddress || memberAddress === previous);

      if (isFollowingEmployee(spouseForm.getValues('residentialAddress'))) {
        spouseForm.setValue('residentialAddress', current);
        spouseForm.setValue('fipsCode', undefined);
        clearSpousePossibleFipsCodes();
      }

      dependentsForm.getValues('dependents').forEach((dependent, index) => {
        if (isFollowingEmployee(dependent.residentialAddress)) {
          dependentsForm.setValue(
            `dependents.${index}.residentialAddress`,
            current
          );
          dependentsForm.setValue(`dependents.${index}.fipsCode`, undefined);
          clearDependentPossibleFipsCodes(index);
        }
      });
    }
  );

  // A member form reset loads saved addresses, so members saved without one
  // are offered the employee's again, as before any edit.
  const spouseDefaults = spouseForm.formState.defaultValues;
  const dependentsDefaults = dependentsForm.formState.defaultValues;

  useEffect(() => {
    // Remembering only non-empty values keeps members following an employee
    // address that is cleared and retyped.
    if (!employeeAddress) {
      return;
    }
    const previous = previousEmployeeAddress.current;
    previousEmployeeAddress.current = employeeAddress;

    if (isEnabled) {
      copyToMembersFollowing(previous, employeeAddress);
    }
  }, [employeeAddress, isEnabled, spouseDefaults, dependentsDefaults]);
};
